import { Box, Button, Flex, Grid, GridItem, Icon, Skeleton, Text } from "@chakra-ui/react";
import { MapPinIcon, XIcon } from "@phosphor-icons/react/dist/ssr";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSearchHover } from "@/pages/Buscar/SearchHoverContext";

import { buildHoverId } from "./buildHoverId";
import EntryCard, { pickPriceField } from "./EntryCard";
import OrderMenu from "./OrderMenu";

// IntersectionObserver-driven infinite scroll. Observer is only created when
// `enabled` is true — once all items are visible we tear it down so it can't
// re-fire forever against a permanently-onscreen last element (which manifested
// as continuously flashing skeletons whenever a filter narrowed results to a
// handful of items).
const useInfiniteScroll = (loadMore, enabled) => {
    const [isFetching, setIsFetching] = useState(false);
    const observer = useRef();

    const lastElementRef = useCallback(
        (node) => {
            if (observer.current) observer.current.disconnect();
            if (!enabled) return;
            observer.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        setIsFetching(true);
                        loadMore(() => setIsFetching(false));
                    }
                },
                { threshold: 0.5 }
            );
            if (node) observer.current.observe(node);
        },
        [loadMore, enabled]
    );

    return [lastElementRef, isFetching];
};

const ITEMS_PER_LOAD = 12;

const ResultsPane = ({
    state,
    setFilter,
    clearFilter,
    properties,
    developments,
    isFetching,
    isWaitingFirstFetch,
    session,
    userFavorites,
    savePropsFavorites,
    saveDevFavorites,
    isCollapsed,
    hidden,
    bounds,
    isMapDragging,
    canAmpliar = false,
    onAmpliar,
    mapHidden = false,
    onShowMap,
    applyBoundsClip = true,
    // User boundary entries only — a dedicated page's default 'floor'
    // boundary is not something the user drew, so the chip stays hidden.
    userPolygons = [],
    onClearBoundary,
}) => {
    const { hoveredId, setHoveredId } = useSearchHover();

    // Unified list. Two sort modes:
    //   Default (state.order == null): developments first (within those,
    //     online_sale_status === "ACTIVE" leads — the "Aparta Online" tag —
    //     so the most sellable devs anchor the grid), then properties in
    //     backend order. Sort is stable.
    //   Explicit (state.order === "ASC" | "DESC"): the dev-leads default is
    //     fully overridden and the unified list is sorted by price across
    //     both kinds. Constraint to keep in mind: this sort runs over the
    //     currently-loaded batch only — infinite-scroll pages get sorted
    //     within themselves but don't re-interleave with already-rendered
    //     items. Acceptable for MVP given the backend page size.
    //     See PROGRESS.md P4.7 for the docs entry.
    const getSortPrice = (entry) => {
        if (entry.kind === "property") {
            return parseFloat(pickPriceField(entry.data, state.operation)) || 0;
        }
        const dev = entry.data;
        return parseFloat(dev.min_presale || dev.min_price || dev.min_sale || dev.min_lease) || 0;
    };

    // Zillow pattern: the list shows only listings whose lat/lng falls inside
    // the current map viewport, so card hover and marker hover correspond 1:1
    // (without this, marker→card highlight paints off-screen cards whenever
    // the result set is larger than the visible card window — verified
    // empirically with hundreds of properties returning a highlight at
    // top:2077px, far below the fold). Items without coords drop out — they
    // aren't mappable, so there's no marker to highlight either. Filter is
    // skipped when `bounds` is null (initial render before the map's first
    // `onIdle`); during that window the unfiltered set is acceptable since
    // the user can't be hovering yet.
    //
    // **applyBoundsClip** is gated by the parent on two things:
    //   1. The map must be visible (mobile list view turns the clip off —
    //      narrowing a list to an invisible rectangle is obscure).
    //   2. The user must have actively dragged or zoomed the camera off
    //      the auto-fit position. Initial / auto-fit / post-Re-centrar state
    //      keeps the clip OFF so the list always shows the full backend set
    //      the polygon/viewport returned. This eliminates the rectangle-vs-
    //      circle corner-gap (a property at the radial perimeter but past
    //      the camera rectangle's short edge would otherwise be in the list
    //      but missing from the map count).
    // When the clip is off, the backend's spatial filter (polygon or viewport
    // lat/lng/radius) is the only spatial layer.
    const inBounds = (entry) => {
        if (!applyBoundsClip || !bounds) return true;
        const lat = parseFloat(entry.data?.location_props?.latitude);
        const lng = parseFloat(entry.data?.location_props?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
        return bounds.contains({ lat, lng });
    };

    // bounds is only a real input when applyBoundsClip is true (see note on
    // the deps array below). Extracted to a variable so the lint rule can
    // statically check the memo's dependencies.
    const boundsDep = applyBoundsClip ? bounds : null;
    const items = useMemo(() => {
        const ps = (properties || []).map((p) => ({ kind: "property", data: p })).filter(inBounds);
        const ds = (developments || [])
            .map((d) => ({ kind: "development", data: d }))
            .filter(inBounds);

        if (state.order === "ASC" || state.order === "DESC") {
            const all = [...ds, ...ps];
            const factor = state.order === "ASC" ? 1 : -1;
            return all.slice().sort((a, b) => factor * (getSortPrice(a) - getSortPrice(b)));
        }

        const sortedDs = ds.slice().sort((a, b) => {
            const aOnline = a.data.online_sale_status === "ACTIVE" ? 1 : 0;
            const bOnline = b.data.online_sale_status === "ACTIVE" ? 1 : 0;
            return bOnline - aOnline;
        });
        return [...sortedDs, ...ps];
        // bounds is only a real input when applyBoundsClip is true. Including
        // it unconditionally would churn the memo on every zoom/drag idle
        // (Google Maps allocates a new LatLngBounds each time), producing a
        // new items array reference with identical contents. That new ref
        // would fire the `useEffect(..., [items])` below, resetting
        // visibleItems to ITEMS_PER_LOAD and making the infinite-scroll
        // observer thrash. Substituting null when the clip is off makes the
        // memo stable across camera idles.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [properties, developments, state.order, state.operation, boundsDep, applyBoundsClip]);

    const [visibleItems, setVisibleItems] = useState(ITEMS_PER_LOAD);

    useEffect(() => {
        setVisibleItems(ITEMS_PER_LOAD);
    }, [items]);

    // Eager-render the first chunk large enough that marker→card highlights
    // can usually land on a mounted card (the implicit D16 contract — list =
    // what's on this map). Rendering the entire filtered set was tried and
    // froze the browser at low zoom (600+ cards mounting at once). 60 covers
    // tight-to-medium viewports completely; beyond that, infinite-scroll
    // takes over as the user scrolls past the chunk.
    const EAGER_RENDER_WHEN_FILTERED = 60;
    // Eager-render only when the bounds-clip is actually narrowing the list
    // (map visible + camera dragged + bounds available). With clip off — list
    // view OR initial / auto-fit / post-Re-centrar state where the list
    // already shows the full backend set — the candidate set is the whole
    // backend page; infinite scroll handles the pagination so we don't mount
    // the entire page at once and freeze narrow phones.
    const effectiveVisible =
        applyBoundsClip && bounds != null
            ? Math.max(visibleItems, Math.min(items.length, EAGER_RENDER_WHEN_FILTERED))
            : visibleItems;

    const loadMore = useCallback((done) => {
        setTimeout(() => {
            setVisibleItems((prev) => prev + ITEMS_PER_LOAD);
            done();
        }, 400);
    }, []);

    const hasMore = effectiveVisible < items.length;
    const [lastElementRef, isLoadingMore] = useInfiniteScroll(loadMore, hasMore);

    if (isCollapsed) return null;

    const visible = items.slice(0, effectiveVisible);
    // Reset to skeleton whenever a query is fetching, not just on first load.
    // Backend is slow enough that letting stale results linger makes filter
    // changes feel laggy ("did my filter take effect?"). A clean skeleton +
    // "Buscando…" header is unambiguous and matches the initial-load look.
    // Skeletons during drag too — no fetch fires, the viewport filter just
    // recomputes, but the visual "drag → blank → refill" rhythm makes the
    // map↔list coupling tangible (Zillow pattern). Otherwise the list
    // rearranging mid-drag reads as a glitch ("cards moved by themselves").
    const showLoadingSkeleton = isWaitingFirstFetch || isFetching || isMapDragging;
    const showEmpty = !showLoadingSkeleton && items.length === 0;
    const totalLabel = items.length === 1 ? "1 resultado" : `${items.length} resultados`;

    return (
        <Box
            // Zillow-style fixed-width pattern: results width matches the
            // card grid, map absorbs the rest.
            //   base (mobile): full width (map hidden)
            //   md - <lg: one card (350) + horizontal padding ≈ 398px
            //   lg+: two cards (700) + gap (16) + padding ≈ 764px
            // Naturally looks 50/50 around xl/2xl; below that the map
            // shrinks while results keep their fixed width.
            w={{ base: "100%", lg: "398px", xl: "764px" }}
            display={hidden ? "none" : "block"}
            css={{
                // 3-col only at QHD+ (≥1920px). Chakra's "2xl" (1536px) is
                // common desktop territory, where 3-col made the cards
                // crowd the map. Custom raw media query because Chakra v3
                // doesn't ship a 3xl/wider breakpoint by default.
                "@media (min-width: 1920px)": {
                    width: "1130px",
                },
            }}
            flex="0 0 auto"
            h="100%"
            overflowY="auto"
            px={6}
            py={4}
        >
            <Flex
                justify="space-between"
                align="center"
                mb={userPolygons.length > 0 ? 2 : 4}
                gap={3}
            >
                <Text fontSize="lg" fontWeight={600} color="gray.800">
                    {showLoadingSkeleton ? "Buscando…" : totalLabel}
                </Text>
                <OrderMenu state={state} setFilter={setFilter} clearFilter={clearFilter} />
            </Flex>
            {/* Active-spatial-filter chip. Makes the polygon stack legible in
                BOTH views — addresses the "I drew a tiny polygon, switched to
                list, and forgot why I only see 2 results" pitfall. ✕ clears
                the entire stack (same as Borrar perímetros). Always rendered
                when user polygons exist; in map view the user can also see
                the polygon outline, so the chip is a redundant but useful
                acknowledgment. */}
            {userPolygons.length > 0 && (
                <Flex
                    align="center"
                    gap={2}
                    bg="gray.100"
                    color="gray.800"
                    borderRadius="full"
                    px={3}
                    py={1.5}
                    mb={4}
                    w="fit-content"
                >
                    <Icon boxSize={4}>
                        <MapPinIcon weight="bold" />
                    </Icon>
                    <Text fontSize="sm" fontWeight={500}>
                        {userPolygons.length === 1
                            ? "1 perímetro dibujado"
                            : `${userPolygons.length} perímetros dibujados`}
                    </Text>
                    {onClearBoundary && (
                        <Box
                            as="button"
                            type="button"
                            onClick={onClearBoundary}
                            aria-label={
                                userPolygons.length === 1 ? "Borrar perímetro" : "Borrar perímetros"
                            }
                            ml={1}
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            w="20px"
                            h="20px"
                            borderRadius="full"
                            color="gray.600"
                            _hover={{ bg: "gray.200", color: "gray.900" }}
                        >
                            <Icon boxSize={3.5}>
                                <XIcon weight="bold" />
                            </Icon>
                        </Box>
                    )}
                </Flex>
            )}

            {showEmpty && (
                <Box py={20} textAlign="center">
                    <Text fontSize="md" color="gray.700" fontWeight={600} mb={1}>
                        No encontramos resultados
                    </Text>
                    {/* Copy + primary CTA branch by context:
                          - canAmpliar (filters or polygons set): primary
                            action resets everything resettable.
                          - !canAmpliar AND mapHidden (mobile list view):
                            primary action toggles to the map so the user
                            has a discovery surface.
                          - !canAmpliar AND map visible: copy points at the
                            map (drag/zoom). No button — map IS the action.
                        "Ingresa una ubicación" hint shows in both
                        !canAmpliar branches, pointing the user back at the
                        always-visible searchbar at the top. */}
                    <Text fontSize="sm" color="gray.500" mb={4}>
                        {canAmpliar
                            ? "Ajusta los filtros o limpia la búsqueda para ver más opciones."
                            : mapHidden
                              ? "Cambia a la vista del mapa o ingresa una ubicación en la búsqueda para explorar otra zona."
                              : "Mueve el mapa o haz zoom, o ingresa una ubicación en la búsqueda, para explorar otra zona."}
                    </Text>
                    {canAmpliar && (
                        <Button
                            onClick={onAmpliar}
                            size="md"
                            bg="colorPalette.solid"
                            color="colorPalette.contrast"
                            borderRadius="full"
                            px={6}
                            _hover={{ bg: "colorPalette.emphasized" }}
                        >
                            Restablecer criterios de búsqueda
                        </Button>
                    )}
                    {!canAmpliar && mapHidden && onShowMap && (
                        <Button
                            onClick={onShowMap}
                            size="md"
                            bg="colorPalette.solid"
                            color="colorPalette.contrast"
                            borderRadius="full"
                            px={6}
                            _hover={{ bg: "colorPalette.emphasized" }}
                        >
                            <Icon mr={2} boxSize={4}>
                                <MapPinIcon weight="bold" />
                            </Icon>
                            Ver mapa
                        </Button>
                    )}
                </Box>
            )}

            <Grid
                // Two regimes, because the map's appearance at lg changes
                // pane width abruptly:
                //   Mobile (no map, <lg) — Zillow-style 1 → 2 → 3 progression:
                //     base (<480):    1 col
                //     sm   (480-767): 2 cols
                //     md   (768-991): 3 cols
                //   Desktop (map shown, lg+):
                //     lg  (992-1279):  1 col + map
                //     xl  (1280-1535): 2 cols + map
                //     2xl (1536+):     3 cols + map
                templateColumns={{
                    // minmax(0, 1fr) at every flexible breakpoint — `1fr`
                    // shorthand resolves to `minmax(auto, 1fr)` where `auto`
                    // is min-content, and FeaturedPropCard / DevsCard
                    // hard-code 250-350px intrinsic widths that would
                    // otherwise grow the track. Was missed at base so the
                    // mobile single-column track grew to ~1090px under
                    // property-only filters (cards visually clipped).
                    base: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    lg: "350px",
                    xl: "repeat(2, 350px)",
                }}
                gap={4}
                justifyContent="center"
                // Cards hard-code 250-350px widths internally
                // (FeaturedPropCard / DevsCard with isForMap). When tracks
                // are 1fr (below lg, in the mobile regime) we need to force
                // cards to fill their column or they sit cramped left.
                // From lg+ the tracks are fixed 350px and the card's
                // hard-coded width is exactly right, so the override stops.
                css={{
                    // Chakra v3 lg is 64em = 1024px (NOT 992). Below lg the
                    // tracks are 1fr, so the cards need to fill their column —
                    // otherwise the hard-coded 350px width spills over into
                    // the next column and adjacent cards overlap by ~22-41px.
                    // Two selector depths because the two card components
                    // wrap their inner Box at different levels:
                    //   DevsCard:         LazyLoad > Box[w=350]
                    //   FeaturedPropCard: LazyLoad > Box[role=list] > Box[w=350]
                    // Hitting both levels normalizes them without touching
                    // the shared card components themselves.
                    "@media (max-width: 1023px)": {
                        "& > * > *, & > * > * > *, & > * > * > * > *": {
                            width: "100% !important",
                            maxWidth: "100%",
                        },
                    },
                    // 3-col only at QHD+ — see the comment on the outer Box.
                    "@media (min-width: 1920px)": {
                        gridTemplateColumns: "repeat(3, 350px)",
                    },
                }}
            >
                {showLoadingSkeleton &&
                    Array.from({ length: 6 }).map((_, i) => (
                        <GridItem key={`skeleton-${i}`}>
                            <Skeleton h="420px" borderRadius="10px" />
                        </GridItem>
                    ))}

                {!showLoadingSkeleton &&
                    visible.map((entry, idx) => {
                        const isLast = idx === visible.length - 1;
                        const ref = isLast ? lastElementRef : null;
                        const hoverId = buildHoverId(entry);
                        const isHovered = hoveredId === hoverId;
                        const item = entry.data;
                        const reactKey =
                            entry.kind === "property"
                                ? `prop-${item.wiggot_code || item.id || idx}`
                                : `dev-${item.id || idx}`;
                        return (
                            <GridItem
                                key={reactKey}
                                ref={ref}
                                borderRadius="10px"
                                transition="box-shadow 0.15s ease"
                                onPointerEnter={() => setHoveredId(hoverId)}
                                onPointerLeave={() => setHoveredId(null)}
                                // Outer box-shadow ring (NOT inset, NOT CSS
                                // outline) so the highlight sits in the 16px
                                // grid gap rather than getting painted over by
                                // the card's opaque bgImage at the inset edge.
                                // Follows the GridItem's borderRadius for free.
                                boxShadow={
                                    isHovered ? "0 0 0 2px var(--chakra-colors-accent-500)" : "none"
                                }
                            >
                                <EntryCard
                                    entry={entry}
                                    idx={idx}
                                    state={state}
                                    session={session}
                                    userFavorites={userFavorites}
                                    savePropsFavorites={savePropsFavorites}
                                    saveDevFavorites={saveDevFavorites}
                                />
                            </GridItem>
                        );
                    })}

                {isLoadingMore &&
                    hasMore &&
                    Array.from({ length: 3 }).map((_, i) => (
                        <GridItem key={`loadmore-${i}`}>
                            <Skeleton h="420px" borderRadius="10px" />
                        </GridItem>
                    ))}
            </Grid>
        </Box>
    );
};

export default ResultsPane;
